import { useLayoutEffect, useRef, useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import "./stepper.css";

// The Ship page's progress steps as a rail on the left of the cards (the
// team's design, 2026-10-19; it was a row of seven steps above the cards).
// Each step's circle sits beside the top of its card (data-ship-step on the
// card); a step without a card on the page (the quote before quoting, Book
// Shipment, Print Label) queues under the one before it, dashed while it is
// still to do (solid once it is the current step or done). Done steps
// show a tick, steps that need attention a "!". Hover or focus shows the
// step's name and state; a click scrolls to its card and moves keyboard
// focus there. Below 768px (stepper.css) the same steps are a row above the
// cards with the current step's name under it, so the cards keep their
// full width there.
//
// The states are the old stepper's, from the same `stepper` object Ship.jsx
// computes: 1 done, 2 problem, the first 0 is the current step (none: the
// last), booleans count as 0 / 1; Book Shipment and Print Label both read
// ShipmentCheck. Pinned by __characterization__/Stepper.test.jsx and
// Ship.stepper.test.jsx (recorded on the old stepper).
const stepsData = [
  { key: "Pickup_Address", label: "Pickup Address", card: "pickup" },
  { key: "Delivery_Address", label: "Delivery Address", card: "delivery" },
  { key: "Additional_services", label: "Additional Services", card: "services" },
  { key: "Packages", label: "Dimension & Weight", card: "packages" },
  { key: "Quote", label: "Select Quote", card: "quote" },
  { key: "ShipmentCheck", label: "Book Shipment", card: null },
  { key: "ShipmentCheck", label: "Print Label", card: null },
];

const STATE_TEXT = {
  done: "done",
  problem: "needs attention",
  current: "next step",
  todo: "to do",
};

// A circle sits this far below its card's top (beside the card title), and
// circles are at least this far apart (Pickup and Delivery share one top).
const NODE_OFFSET = 10;
const MIN_GAP = 38;
// The sticky navbar's height plus a little room, as the Fulfilment Location
// card's own scroll (scroll-margin-top 72px).
const SCROLL_OFFSET = 72;

const cardFor = (rail, step) =>
  step.card ? rail?.parentElement?.querySelector(`[data-ship-step="${step.card}"]`) : null;
const isShown = (el) => Boolean(el && el.getClientRects?.().length);

// The rail sits this far left of the cards' (or the invoice's) edge: its
// 28px width and a 12px gap.
const RAIL_GAP = 40;

// Each step's top in the rail, whether its card is on the page, whether the
// circle sits right at its card (then a short tick joins them) and the
// tick's length to the card's real edge (the Shipment type card is 90% wide
// above 992px, not col-md-11). After a booking the invoice replaces the
// cards (FullPageInvoice's visible box; its hidden print copy has another
// class): the steps then line up beside the invoice, from its top, wherever
// it is centred (`left`, else the CSS's).
function measure(rail, setLayout) {
  if (!rail) return;
  const page = rail.parentElement;
  const box = rail.getBoundingClientRect();
  const base = box.top;
  const invoice = page?.querySelector(".shipment__fp_invoice_custom_wrap");
  const beside = isShown(invoice) ? invoice.getBoundingClientRect() : null;
  const left = beside
    ? Math.max(8, Math.round(beside.left - page.getBoundingClientRect().left - RAIL_GAP))
    : null;
  const tops = [];
  const shown = [];
  const at = [];
  const ticks = [];
  let prev = null;
  stepsData.forEach((step) => {
    const card = cardFor(rail, step);
    const visible = isShown(card);
    const cardBox = visible ? card.getBoundingClientRect() : null;
    const want = visible
      ? cardBox.top - base + NODE_OFFSET
      : prev === null
        ? beside
          ? beside.top - base + NODE_OFFSET
          : 0
        : prev + MIN_GAP + 6;
    const top = prev === null ? want : Math.max(want, prev + MIN_GAP);
    tops.push(Math.round(top));
    shown.push(visible);
    at.push(visible && Math.abs(top - want) < 1);
    // from the circle's right edge (26px into the rail) to the card
    ticks.push(visible ? Math.max(4, Math.round(cardBox.left - box.left - 26)) : 0);
    prev = top;
  });
  setLayout((current) =>
    current.left === left &&
    current.tops.join() === tops.join() &&
    current.shown.join() === shown.join() &&
    current.at.join() === at.join() &&
    current.ticks.join() === ticks.join()
      ? current
      : { left, tops, shown, at, ticks }
  );
}

export default function Stepper({ stepper = {} }) {
  const railRef = useRef(null);
  const [layout, setLayout] = useState({ left: null, tops: [], shown: [], at: [], ticks: [] });

  const getStepStatus = (key) => {
    const value = stepper?.[key];
    if (typeof value === "boolean") return value ? 1 : 0;
    return value ?? 0;
  };
  const statuses = stepsData.map((step) => getStepStatus(step.key));
  // First incomplete step becomes active
  let activeIndex = statuses.findIndex((status) => status === 0);
  if (activeIndex === -1) activeIndex = stepsData.length - 1;
  const states = statuses.map((status, index) =>
    status === 1 ? "done" : status === 2 ? "problem" : index === activeIndex ? "current" : "todo"
  );

  // Whenever Ship works the steps out again (it does on every change that
  // shows or hides a card: addresses expanding, a quote, the customs form,
  // a booking), and whenever the page's size changes (rows added, Saved
  // items' views) or the window's does. Only a changed layout re-renders.
  useLayoutEffect(() => {
    measure(railRef.current, setLayout);
  }, [stepper]);
  useLayoutEffect(() => {
    const rail = railRef.current;
    const page = rail?.parentElement;
    const run = () => measure(rail, setLayout);
    let observer;
    if (typeof ResizeObserver !== "undefined" && page) {
      observer = new ResizeObserver(run);
      observer.observe(page);
    }
    window.addEventListener("resize", run);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", run);
    };
  }, []);

  // Scroll the card under the navbar, then move keyboard focus to it (as the
  // Fulfilment Location card's own jump does), so keyboard and screen-reader
  // users carry on from there. The card takes focus only for that moment.
  const jump = (step) => {
    const card = cardFor(railRef.current, step);
    if (!isShown(card)) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    window.scrollTo({
      top: card.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET,
      behavior: reduce ? "auto" : "smooth",
    });
    if (!card.hasAttribute("tabindex")) {
      card.setAttribute("tabindex", "-1");
      card.addEventListener("blur", () => card.removeAttribute("tabindex"), { once: true });
    }
    card.focus({ preventScroll: true });
  };

  const { left, tops, shown, at, ticks } = layout;
  const reached = (index) => states[index] === "done" || states[index] === "current";
  const pending = (index) => !shown[index] && !reached(index);
  const last = stepsData.length - 1;

  // Under the row on phones: "Step 3 of 7 · Additional Services", or, with no
  // step left to do, the ones that need attention or "All steps done".
  const current = states.indexOf("current");
  const problems = states.filter((state) => state === "problem").length;
  const nowText =
    current >= 0
      ? `Step ${current + 1} of ${stepsData.length} · ${stepsData[current].label}`
      : problems > 0
        ? `${problems} ${problems > 1 ? "steps need" : "step needs"} attention`
        : "All steps done";

  return (
    <aside
      className="shiprail"
      ref={railRef}
      aria-label="Shipment progress"
      style={left === null ? undefined : { left }}
    >
      <ol className="shiprail__list" style={{ height: (tops[last] ?? 0) + 30 }}>
        {stepsData.map((step, index) => {
          const state = states[index];
          const classes = [
            "shiprail__step",
            `is-${state}`,
            pending(index) && "is-pending",
            at[index] && "is-at",
            index < last && states[index] === "done" && states[index + 1] === "done" && "seg-done",
            index < last && pending(index + 1) && "seg-pending",
          ].filter(Boolean);
          return (
            <li
              key={step.label}
              className={classes.join(" ")}
              style={{
                top: tops[index] ?? index * (MIN_GAP + 6),
                "--seg": index < last ? `${(tops[index + 1] ?? 0) - (tops[index] ?? 0)}px` : undefined,
                "--tick": ticks[index] ? `${ticks[index]}px` : undefined,
              }}
            >
              <button
                type="button"
                className="shiprail__btn"
                data-step-state={state}
                data-step-label={step.label}
                aria-label={`${step.label}: ${STATE_TEXT[state]}`}
                aria-current={state === "current" ? "step" : undefined}
                aria-disabled={shown[index] ? undefined : true}
                // a step with nowhere to go stays out of the Tab order (screen
                // readers still list it); the others come before the form
                tabIndex={shown[index] ? undefined : -1}
                onClick={() => jump(step)}
              >
                <span className="shiprail__dot" aria-hidden="true">
                  {state === "done" ? <GiCheckMark /> : state === "problem" ? "!" : index + 1}
                </span>
                <span className="shiprail__tip" aria-hidden="true">
                  {step.label}
                  <span className="shiprail__tip-state">{STATE_TEXT[state]}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <p className="shiprail__now" aria-hidden="true">
        {nowText}
      </p>
    </aside>
  );
}
